import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ProductCard from '@/components/ProductCard';
import api from '@/lib/api';

const PAGE_SIZE = 24;

export default function ShopPage() {
    const { categorySlug } = useParams();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('created_at');
    const [selectedCategory, setSelectedCategory] = useState(categorySlug || '');
    const sentinelRef = useRef(null);

    useEffect(() => {
        api.get('/categories').then(({ data }) => setCategories(data)).catch(() => {});
    }, []);

    // Reset list when filters change (category, search, sort)
    useEffect(() => {
        setSelectedCategory(categorySlug || '');
    }, [categorySlug]);

    const fetchPage = useCallback(async (pageNum, replace) => {
        const params = new URLSearchParams();
        if (selectedCategory) params.set('category', selectedCategory);
        if (searchQuery) params.set('search', searchQuery);
        params.set('sort_by', sortBy);
        params.set('page', pageNum);
        params.set('limit', PAGE_SIZE);
        try {
            const { data } = await api.get(`/products?${params}`);
            setTotal(data.total || 0);
            setProducts(prev => replace ? (data.products || []) : [...prev, ...(data.products || [])]);
            setPage(pageNum);
        } catch { /* swallow */ }
    }, [selectedCategory, searchQuery, sortBy]);

    // Initial / filter-change load — replace list
    useEffect(() => {
        setLoading(true);
        setProducts([]);
        setPage(1);
        fetchPage(1, true).finally(() => setLoading(false));
    }, [fetchPage]);

    const hasMore = products.length < total;

    const loadMore = useCallback(async () => {
        if (loadingMore || loading || !hasMore) return;
        setLoadingMore(true);
        await fetchPage(page + 1, false);
        setLoadingMore(false);
    }, [loadingMore, loading, hasMore, page, fetchPage]);

    // IntersectionObserver — auto-load when the sentinel scrolls into view
    useEffect(() => {
        if (!sentinelRef.current) return;
        const obs = new IntersectionObserver(entries => {
            if (entries[0]?.isIntersecting) loadMore();
        }, { rootMargin: '400px 0px' });
        obs.observe(sentinelRef.current);
        return () => obs.disconnect();
    }, [loadMore]);

    const currentCat = categories.find(c => c.slug === selectedCategory || c.id === selectedCategory);

    return (
        <div className="min-h-screen bg-gray-50" data-testid="shop-page">
            {/* Breadcrumb & Header */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center gap-2 text-sm font-body text-gray-500 mb-2">
                        <Link to="/" className="hover:text-pink-600">Home</Link>
                        <span>/</span>
                        <span className="text-gray-900">Shop</span>
                        {currentCat && <><span>/</span><span className="text-gray-900">{currentCat.name}</span></>}
                    </div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
                        {searchQuery ? `Search: "${searchQuery}"` : currentCat ? currentCat.name : 'All Products'}
                    </h1>
                    <p className="text-sm font-body text-gray-500 mt-1" data-testid="shop-total">{total} product{total !== 1 ? 's' : ''} found</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Filter bar */}
                <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                    <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
                        <button onClick={() => setSelectedCategory('')}
                            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-body font-medium transition-all ${!selectedCategory ? 'bg-pink-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-pink-300'}`}
                            data-testid="filter-all">All</button>
                        {categories.map(cat => (
                            <button key={cat.id} onClick={() => setSelectedCategory(cat.slug)}
                                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-body font-medium transition-all ${selectedCategory === cat.slug ? 'bg-pink-600 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-pink-300'}`}
                                data-testid={`filter-${cat.slug}`}>{cat.name}</button>
                        ))}
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-44 rounded-full font-body" data-testid="sort-select">
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="created_at">Newest</SelectItem>
                            <SelectItem value="price_low">Price: Low to High</SelectItem>
                            <SelectItem value="price_high">Price: High to Low</SelectItem>
                            <SelectItem value="name">Name A-Z</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="bg-white rounded-xl overflow-hidden border animate-pulse">
                                <div className="aspect-square bg-gray-200" />
                                <div className="p-4 space-y-2"><div className="h-3 bg-gray-200 rounded w-1/2" /><div className="h-4 bg-gray-200 rounded" /><div className="h-4 bg-gray-200 rounded w-2/3" /></div>
                            </div>
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-xl font-body text-gray-400 mb-4">No products found</p>
                        <Button onClick={() => setSelectedCategory('')} className="rounded-full bg-pink-600 hover:bg-pink-700 font-body">View All Products</Button>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 stagger-children" data-testid="products-grid">
                            {products.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 6} />)}
                        </div>

                        {/* Infinite-scroll sentinel + manual fallback */}
                        {hasMore && (
                            <div ref={sentinelRef} className="flex justify-center py-8" data-testid="shop-infinite-sentinel">
                                {loadingMore ? (
                                    <div className="flex items-center gap-2 text-pink-600 font-body text-sm" data-testid="shop-loading-more">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Loading more products…
                                    </div>
                                ) : (
                                    <Button
                                        onClick={loadMore}
                                        variant="outline"
                                        className="rounded-full font-body border-pink-200 text-pink-700 hover:bg-pink-50 hover:text-pink-800 hover:border-pink-300 px-6"
                                        data-testid="shop-load-more"
                                    >
                                        Load more ({total - products.length} remaining)
                                    </Button>
                                )}
                            </div>
                        )}
                        {!hasMore && products.length > 0 && total > PAGE_SIZE && (
                            <p className="text-center text-xs font-body text-gray-400 py-8" data-testid="shop-end">— End of catalog ({total} products) —</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
